import type { EventBus } from '@/core/EventBus';
import type { GameController } from '@/game/GameController';
import type { PlayerService } from '@/player/PlayerService';
import { UNIT_DEFINITIONS } from '@/units/UnitDefinitions';
import { ShopEvents, type ShopSlot } from '@/shop/ShopSystem';
import { GameStateEvents, GamePhase } from '@/game/GameStateManager';
import { EconomyEvents } from '@/economy/CurrencyManager';

/**
 * Shop Panel UI - displays purchasable units at the bottom of the screen
 */
export class ShopPanel {
  private container: HTMLElement;
  private eventBus: EventBus;
  private gameController: GameController;
  private playerService: PlayerService | null = null;
  private panelElement: HTMLElement | null = null;
  private isVisible = false;
  private selectedSlotIndex: number | null = null;

  constructor(
    container: HTMLElement,
    eventBus: EventBus,
    gameController: GameController,
    playerService?: PlayerService
  ) {
    this.container = container;
    this.eventBus = eventBus;
    this.gameController = gameController;
    this.playerService = playerService || null;

    this.createPanel();
    this.setupEventListeners();

    // Check if already in preparation phase (for multiplayer where events may have fired)
    const currentPhase = this.gameController.getGameInfo().phase;
    if (currentPhase === GamePhase.Preparation) {
      this.show();
      this.updateShop();
    }
  }

  /**
   * Create the panel UI
   */
  private createPanel(): void {
    this.panelElement = document.createElement('div');
    this.panelElement.id = 'shop-panel';
    this.panelElement.innerHTML = `
      <style>
        #shop-panel {
          position: absolute;
          bottom: env(safe-area-inset-bottom, 0px);
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.85);
          border-radius: 0;
          padding: 12px 16px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: white;
          display: none;
          z-index: 100;
          max-width: 100vw;
          box-sizing: border-box;
        }
        #shop-panel.visible {
          display: block;
        }
        .shop-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding-bottom: 8px;
        }
        .shop-title {
          font-size: 14px;
          font-weight: bold;
          text-transform: uppercase;
          color: #ffd700;
        }
        .shop-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .shop-gold {
          color: #ffd700;
          font-weight: bold;
          font-size: 14px;
        }
        .shop-btn {
          padding: 6px 12px;
          font-size: 12px;
          font-weight: bold;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .shop-btn:hover:not(:disabled) {
          transform: scale(1.05);
        }
        .shop-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .shop-btn.reroll {
          background: #3182ce;
          color: white;
        }
        .shop-btn.lock {
          background: #4a5568;
          color: white;
        }
        .shop-btn.lock.locked {
          background: #e53e3e;
        }
        .shop-slots {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .shop-slot {
          width: 120px;
          background: rgba(74, 85, 104, 0.6);
          border-radius: 8px;
          padding: 10px;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: center;
        }
        .shop-slot:hover:not(.empty):not(.purchased) {
          background: rgba(74, 85, 104, 0.9);
          border-color: #63b3ed;
          transform: translateY(-2px);
        }
        .shop-slot.purchased {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .shop-slot.empty {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .shop-slot.cant-afford {
          border-color: #e53e3e;
        }
        .shop-slot.selected {
          border-color: #ffd700;
          box-shadow: 0 0 12px rgba(255, 215, 0, 0.5);
          transform: translateY(-4px);
        }
        .slot-unit-name {
          font-weight: bold;
          font-size: 13px;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .slot-unit-tier {
          color: #ffd700;
          font-size: 11px;
          margin-bottom: 6px;
        }
        .slot-unit-stats {
          display: flex;
          justify-content: center;
          gap: 6px;
          font-size: 10px;
          color: #a0aec0;
          margin-bottom: 6px;
        }
        .slot-stat-hp { color: #68d391; }
        .slot-stat-atk { color: #fc8181; }
        .slot-stat-def { color: #63b3ed; }
        .slot-unit-traits {
          display: flex;
          gap: 3px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 8px;
        }
        .slot-trait {
          font-size: 9px;
          background: rgba(255, 255, 255, 0.15);
          padding: 1px 4px;
          border-radius: 3px;
          text-transform: capitalize;
        }
        .slot-cost {
          font-weight: bold;
          font-size: 14px;
          color: #ffd700;
        }
        .slot-cost::before {
          content: '';
        }
        .slot-empty-text {
          color: #718096;
          font-size: 12px;
        }
        .slot-color-bar {
          height: 4px;
          border-radius: 2px;
          margin-bottom: 6px;
        }
        .shop-players {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          font-size: 14px;
          font-weight: 600;
        }
        .shop-player-entry {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .shop-player-name {
          padding: 4px 8px;
          border-radius: 4px;
        }
        .shop-player-name.player1 {
          color: #3b82f6;
        }
        .shop-player-name.player2 {
          color: #ef4444;
        }
        .shop-player-score {
          font-size: 18px;
          font-weight: 700;
        }
        .shop-player-score.player1 {
          color: #3b82f6;
        }
        .shop-player-score.player2 {
          color: #ef4444;
        }
        .shop-vs {
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
        }
      </style>
      <div class="shop-players">
        <div class="shop-player-entry">
          <span class="shop-player-name player1" id="shop-player1-name">Player 1</span>
          <span class="shop-player-score player1" id="shop-player1-score">0</span>
        </div>
        <span class="shop-vs">vs</span>
        <div class="shop-player-entry">
          <span class="shop-player-score player2" id="shop-player2-score">0</span>
          <span class="shop-player-name player2" id="shop-player2-name">Player 2</span>
        </div>
      </div>
      <div class="shop-header">
        <span class="shop-title">Shop</span>
        <div class="shop-controls">
          <span class="shop-gold" id="shop-gold">10 Gold</span>
          <button class="shop-btn reroll" id="shop-reroll">Reroll (1)</button>
          <button class="shop-btn lock" id="shop-lock">Lock</button>
        </div>
      </div>
      <div class="shop-slots" id="shop-slots"></div>
    `;

    this.container.appendChild(this.panelElement);
    this.setupButtonHandlers();
  }

  /**
   * Setup button click handlers
   */
  private setupButtonHandlers(): void {
    const rerollBtn = document.getElementById('shop-reroll');
    const lockBtn = document.getElementById('shop-lock');

    rerollBtn?.addEventListener('click', () => {
      this.gameController.rerollShop(this.getLocalPlayerId());
    });

    lockBtn?.addEventListener('click', () => {
      const isLocked = this.gameController.toggleShopLock(this.getLocalPlayerId());
      this.updateLockButton(isLocked);
    });
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Show/hide on phase change
    this.eventBus.on<{ previousPhase: GamePhase; newPhase: GamePhase }>(
      GameStateEvents.PHASE_CHANGED,
      (data) => {
        if (data.newPhase === GamePhase.Preparation) {
          this.show();
          this.updateShop();
        } else {
          this.hide();
        }
      }
    );

    // Update on shop refresh
    this.eventBus.on(ShopEvents.SHOP_REFRESHED, () => {
      this.updateShop();
    });

    // Update on unit bought and deselect
    this.eventBus.on(ShopEvents.UNIT_BOUGHT, () => {
      this.deselectSlot();
      this.updateShop();
    });

    // Update gold display
    this.eventBus.on(EconomyEvents.GOLD_CHANGED, () => {
      this.updateGold();
      this.updateSlotAffordability();
    });

    // Update lock state
    this.eventBus.on(ShopEvents.SHOP_LOCKED, () => {
      this.updateLockButton(true);
    });

    this.eventBus.on(ShopEvents.SHOP_UNLOCKED, () => {
      this.updateLockButton(false);
    });
  }

  /**
   * Get the local player ID (supports multiplayer)
   */
  private getLocalPlayerId(): number {
    return this.gameController.isMultiplayerMode()
      ? this.gameController.getLocalPlayerId()
      : 1;
  }

  /**
   * Get gold for the local player
   */
  private getLocalGold(): number {
    const playerId = this.getLocalPlayerId();
    const info = this.gameController.getGameInfo();
    return playerId === 1 ? info.player1Gold : info.player2Gold;
  }

  /**
   * Update the shop display
   */
  updateShop(): void {
    const slotsContainer = document.getElementById('shop-slots');
    if (!slotsContainer) return;

    const playerId = this.getLocalPlayerId();
    const shopState = this.gameController.getShopState(playerId);
    const gold = this.getLocalGold();

    slotsContainer.innerHTML = '';

    for (let i = 0; i < shopState.slots.length; i++) {
      const slot = shopState.slots[i];
      const slotElement = this.createSlotElement(slot, i, gold);
      slotsContainer.appendChild(slotElement);
    }

    this.updateGold();
    this.updateLockButton(shopState.locked);
  }

  /**
   * Create a shop slot element
   */
  private createSlotElement(slot: ShopSlot, index: number, gold: number): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.className = 'shop-slot';
    slotEl.dataset.slotIndex = String(index);

    if (!slot.unitType) {
      slotEl.classList.add('empty');
      slotEl.innerHTML = `<span class="slot-empty-text">Empty</span>`;
      return slotEl;
    }

    if (slot.purchased) {
      slotEl.classList.add('purchased');
    }

    const cantAfford = gold < slot.cost;
    if (cantAfford && !slot.purchased) {
      slotEl.classList.add('cant-afford');
    }

    const unit = UNIT_DEFINITIONS[slot.unitType];
    if (!unit) {
      slotEl.classList.add('empty');
      slotEl.innerHTML = `<span class="slot-empty-text">???</span>`;
      return slotEl;
    }

    const tierStars = '\u2605'.repeat(unit.tier);
    const colorHex = '#' + unit.color.toString(16).padStart(6, '0');

    slotEl.innerHTML = `
      <div class="slot-color-bar" style="background-color: ${colorHex}"></div>
      <div class="slot-unit-name">${unit.name}</div>
      <div class="slot-unit-tier">${tierStars}</div>
      <div class="slot-unit-stats">
        <span class="slot-stat-hp">HP:${unit.baseStats.maxHealth}</span>
        <span class="slot-stat-atk">ATK:${unit.baseStats.attack}</span>
        <span class="slot-stat-def">DEF:${unit.baseStats.defense}</span>
      </div>
      <div class="slot-unit-traits">
        ${unit.traits.map((t) => `<span class="slot-trait">${t}</span>`).join('')}
      </div>
      <div class="slot-cost">${slot.cost} Gold</div>
    `;

    // Click handler to select for placement
    if (!slot.purchased) {
      slotEl.addEventListener('click', () => {
        this.onSlotClick(index);
      });
    }

    return slotEl;
  }

  /**
   * Handle slot click - select/deselect for placement
   */
  private onSlotClick(slotIndex: number): void {
    const shopState = this.gameController.getShopState(this.getLocalPlayerId());
    const slot = shopState.slots[slotIndex];
    const gold = this.getLocalGold();

    if (!slot || !slot.unitType || slot.purchased) return;
    if (gold < slot.cost) return;

    if (this.selectedSlotIndex === slotIndex) {
      this.deselectSlot();
    } else {
      this.selectSlot(slotIndex);
    }
  }

  /**
   * Select a shop slot for placement
   */
  selectSlot(slotIndex: number): void {
    // Deselect previous
    this.deselectSlot();

    const shopState = this.gameController.getShopState(this.getLocalPlayerId());
    const slot = shopState.slots[slotIndex];
    if (!slot || !slot.unitType) return;

    this.selectedSlotIndex = slotIndex;

    // Update UI
    const slotEl = this.panelElement?.querySelector(`.shop-slot[data-slot-index="${slotIndex}"]`);
    slotEl?.classList.add('selected');

    // Emit event for PlacementSystem
    this.eventBus.emit(ShopEvents.SLOT_SELECTED, {
      slotIndex,
      unitType: slot.unitType,
      cost: slot.cost,
    });
  }

  /**
   * Deselect current slot
   */
  deselectSlot(): void {
    if (this.selectedSlotIndex === null) return;

    const slotEl = this.panelElement?.querySelector(`.shop-slot[data-slot-index="${this.selectedSlotIndex}"]`);
    slotEl?.classList.remove('selected');

    this.selectedSlotIndex = null;

    this.eventBus.emit(ShopEvents.SLOT_DESELECTED, {});
  }

  /**
   * Get the currently selected slot index
   */
  getSelectedSlotIndex(): number | null {
    return this.selectedSlotIndex;
  }

  /**
   * Update player names and scores display
   */
  private updatePlayerNames(): void {
    const p1NameEl = document.getElementById('shop-player1-name');
    const p2NameEl = document.getElementById('shop-player2-name');
    const p1ScoreEl = document.getElementById('shop-player1-score');
    const p2ScoreEl = document.getElementById('shop-player2-score');
    if (!p1NameEl || !p2NameEl) return;

    const localPlayerName = this.playerService?.getName() || 'You';
    const opponentName = this.gameController.getOpponentName();
    const localPlayerId = this.gameController.getLocalPlayerId();
    const info = this.gameController.getGameInfo();

    // Local player always shown first (left side)
    p1NameEl.textContent = localPlayerName;
    p2NameEl.textContent = opponentName;

    // Scores: local player's score on left, opponent's on right
    const localScore = localPlayerId === 1 ? info.scores.player1 : info.scores.player2;
    const opponentScore = localPlayerId === 1 ? info.scores.player2 : info.scores.player1;
    if (p1ScoreEl) p1ScoreEl.textContent = String(localScore);
    if (p2ScoreEl) p2ScoreEl.textContent = String(opponentScore);

    // Set colors based on actual player IDs
    if (localPlayerId === 1) {
      p1NameEl.className = 'shop-player-name player1';
      p2NameEl.className = 'shop-player-name player2';
      if (p1ScoreEl) p1ScoreEl.className = 'shop-player-score player1';
      if (p2ScoreEl) p2ScoreEl.className = 'shop-player-score player2';
    } else {
      p1NameEl.className = 'shop-player-name player2';
      p2NameEl.className = 'shop-player-name player1';
      if (p1ScoreEl) p1ScoreEl.className = 'shop-player-score player2';
      if (p2ScoreEl) p2ScoreEl.className = 'shop-player-score player1';
    }
  }

  /**
   * Update gold display
   */
  private updateGold(): void {
    const goldEl = document.getElementById('shop-gold');
    if (goldEl) {
      const gold = this.getLocalGold();
      goldEl.textContent = `${gold} Gold`;
    }
  }

  /**
   * Update slot affordability styling
   */
  private updateSlotAffordability(): void {
    const gold = this.getLocalGold();
    const shopState = this.gameController.getShopState(this.getLocalPlayerId());
    const slots = document.querySelectorAll('#shop-slots .shop-slot');

    slots.forEach((slotEl, i) => {
      const slot = shopState.slots[i];
      if (slot && slot.unitType && !slot.purchased) {
        if (gold < slot.cost) {
          slotEl.classList.add('cant-afford');
        } else {
          slotEl.classList.remove('cant-afford');
        }
      }
    });
  }

  /**
   * Update lock button state
   */
  private updateLockButton(isLocked: boolean): void {
    const lockBtn = document.getElementById('shop-lock');
    if (lockBtn) {
      lockBtn.textContent = isLocked ? 'Locked' : 'Lock';
      if (isLocked) {
        lockBtn.classList.add('locked');
      } else {
        lockBtn.classList.remove('locked');
      }
    }
  }

  /**
   * Show the panel
   */
  show(): void {
    this.isVisible = true;
    this.panelElement?.classList.add('visible');
    this.updatePlayerNames();
  }

  /**
   * Hide the panel
   */
  hide(): void {
    this.isVisible = false;
    this.deselectSlot();
    this.panelElement?.classList.remove('visible');
  }

  /**
   * Check if panel is visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Clean up
   */
  dispose(): void {
    if (this.panelElement) {
      this.container.removeChild(this.panelElement);
    }
  }
}
